import mojs from '@mojs/core/dist/mo.umd.js';

class Animate {
	constructor(config) {
		this.config = Object.assign({
			scope: '[data-animate]',
			delay: 0,
			duration: 500,
			groupedDelay: 200,
			defaultBlur: 5,
		}, config);

		this.$scope = $(this.config.scope);
		this.groups = {};
		this.yElements = [];

		// Lijst van interne/ongewenste keys die NIET naar mojs doorgesluisd mogen worden
		this.excludedDataKeys = new Set([
			'animate',
			'animateGroup',
			'animateYOnly',
			'prio',
			'groupedDelay',
			'isAnimated',
			'inViewport',
			'timeline',
			'animation',
			'blur',
		]);

		if (this.$scope.length > 0) {
			this.init();
		}
	}

	init() {
		this.observer = new IntersectionObserver((entries) => {
			let affectedGroups = new Set();

			entries.forEach(entry => {
				let $scope = $(entry.target);
				$scope.data('in-viewport', entry.isIntersecting);

				if (!entry.isIntersecting) return;
				if ($scope.data('is-animated') === true) return;

				let groupName = $scope.data('animate-group');
				if (groupName) {
					affectedGroups.add(groupName);
				} else {
					$scope.data('is-animated', true);
					this.animate($scope);
					this.observer.unobserve(entry.target);
				}
			});

			affectedGroups.forEach(groupName => this.animateGroup(groupName));
		}, { threshold: 0 });

		this.$scope.each((index, scope) => {
			let $scope = $(scope);
			let groupName = $scope.data('animate-group');

			this.setInitialState($scope);

			if (groupName) {
				if (!this.groups[groupName]) {
					this.groups[groupName] = [];
				}
				this.groups[groupName].push($scope);
			}

			if ($scope.data('animate-y-only') !== undefined) {
				this.yElements.push($scope);
			} else {
				this.observer.observe(scope);
			}
		});

		this.initYObservers();
	}

	initYObservers() {
		if (this.yElements.length === 0) return;

		let check = () => {
			let affectedGroups = new Set();

			this.yElements.forEach($scope => {
				if ($scope.data('is-animated') === true) return;

				const rect = $scope[0].getBoundingClientRect();
				const inViewport = rect.bottom > 0 && rect.top < window.innerHeight;

				$scope.data('in-viewport', inViewport);

				if (!inViewport) return;

				let groupName = $scope.data('animate-group');
				if (groupName) {
					affectedGroups.add(groupName);
				} else {
					$scope.data('is-animated', true);
					this.animate($scope);
				}
			});

			affectedGroups.forEach(groupName => this.animateGroup(groupName));
		};

		check();
		let ticking = false;
		$(window).on('scroll', () => {
			if (!ticking) {
				requestAnimationFrame(() => { check(); ticking = false; });
				ticking = true;
			}
		});
	}

	animateGroup(groupName) {
		let members = this.groups[groupName];
		if (!members) return;

		let toAnimate = members.filter($el => {
			return $el.data('is-animated') !== true && $el.data('in-viewport') === true;
		}).sort(($a, $b) => {
			let prioA = $a.data('prio');
			let prioB = $b.data('prio');
			prioA = prioA === 'first' ? -Infinity : prioA === 'last' ? Infinity : (prioA || 0);
			prioB = prioB === 'first' ? -Infinity : prioB === 'last' ? Infinity : (prioB || 0);
			return prioA - prioB;
		});

		if (toAnimate.length === 0) return;

		let cumulativeDelay = 0;
		toAnimate.forEach(($el, index) => {
			if (index > 0) {
				cumulativeDelay += $el.data('grouped-delay') ?? this.config.groupedDelay;
			}
			let delay = cumulativeDelay;
			$el.data('is-animated', true);
			this.observer.unobserve($el[0]);

			this.setAnimation($el);

			// Via requestAnimationFrame zodat mojs een frame heeft
			// om z'n interne state op te zetten na new mojs.Html()
			if (delay > 0) {
				setTimeout(() => requestAnimationFrame(() => this.animate($el)), delay);
			} else {
				requestAnimationFrame(() => this.animate($el));
			}
		});
	}

	#isLowEnd() {
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;

		// deviceMemory is Chrome only maar betrouwbaarder dan cores
		if (navigator.deviceMemory && navigator.deviceMemory <= 2) return true;

		// Alleen echt trage verbindingen, niet als hardware-indicator
		const conn = navigator.connection?.effectiveType;
		if (conn === '2g' || conn === 'slow-2g') return true;

		return false;
	}

	setInitialState($scope) {
		if (this.#isLowEnd()) return;

		const blur = $scope.data('blur') ?? { [this.config.defaultBlur]: 0 };
		const from = parseFloat(Object.keys(blur)[0]);

		if (from > 0) {
			$scope.css('filter', `blur(${from}px)`);
		}
	}

	/**
	 * Bouwt een schoon settings-object op voor mojs.Html.
	 * Pakt alleen de data-* attributen, filtert alle interne/jQuery keys eruit.
	 */
	buildMojsSettings($scope) {
		const skipBlur = this.#isLowEnd();
		const blur = !skipBlur ? ($scope.data('blur') ?? { [this.config.defaultBlur]: 0 }) : { 0: 0 };
		let from = parseFloat(Object.keys(blur)[0]);
		let to = parseFloat(Object.values(blur)[0]);

		let defaults = {
			el: $scope[0],
			duration: this.config.duration,
			easing: 'ease.out',
			delay: this.config.delay,
			customProperties: {
				blur: {
					type: 'number',
					default: from,
				}
			},
			blur: { [from]: to },
			onUpdate: function() {
				this.el.style.filter = `blur(${this._props.blur}px)`;
			},
			onComplete: function() {
				if (to === 0) {
					this.el.style.filter = '';
				} else {
					this.el.style.filter = `blur(${to}px)`;
				}
				this.el.style.willChange = 'auto';
			},
		};

		let data = $scope.data();
		let clean = {};

		for (let key in data) {
			if (this.excludedDataKeys.has(key)) continue;

			let value = data[key];

			// jQuery-interne objecten en DOM-referenties overslaan
			if (value instanceof HTMLElement) continue;
			if (value instanceof $) continue;
			if (typeof value === 'function') continue;

			// mojs-objecten (eerder opgeslagen animaties etc.) overslaan
			if (value !== null && typeof value === 'object' && value.constructor &&
				value.constructor.name !== 'Object' && !Array.isArray(value)) {
				continue;
			}

			clean[key] = value;
		}

		clean.blur = { [from]: to };

		return Object.assign(defaults, clean);
	}

	setAnimation($scope) {
		let settings = this.buildMojsSettings($scope);
		let animation = new mojs.Html(settings);
		$scope.data('animation', animation);
	}

	animate($scope) {
		if (!$scope.data('animation')) {
			this.setAnimation($scope);
		}
		$scope.css('will-change', 'filter, transform, opacity');
		$scope.data('animation').play();
	}

	getOffsetY($scope) {
		let data = $scope.data('y');
		let y = 0;
		if (data) {
			y = Object.keys(data)[0];
		}
		return y;
	}
}

export default new Animate();
